import { zodResolver } from '@hookform/resolvers/zod';
import {
  TaxIdentityController_list_v1,
  TaxIdentityController_replace_v1,
  type TaxIdentityController_replace_v1Body,
  type TaxIdentityResource,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { optionalGstinSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatInstant } from '@/lib/instant';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { EmptyState, LoadFailed, Screen, ScreenNote } from '../back-office/Screen';

/**
 * The park's GST registrations (#321, blossom-rest-api#534): each one's
 * registered name, address, GSTIN and invoice title. Every outlet names one,
 * and its tills print that one at the top of a tax invoice — so Green Walk's
 * outlets print Green Walk's GSTIN and STJ Traders' print theirs.
 *
 * **Managers read, owners write**, mirroring `GET /v1/tax-identities` (both)
 * and `PUT /v1/tax-identities/{id}` (owner). A manager is shown the same
 * registrations as plain values with no field and no button. That is courtesy,
 * not security: the role comes from `sessionStorage`, and the API refuses a
 * manager's `PUT` regardless.
 *
 * **`PUT` replaces all four**, so the body always states all four — a field
 * nobody touched is sent as it stands, and a blank one goes as unset.
 *
 * **The GSTIN is behind its own step.** It is the one value the owner has
 * confirmed, and a typo in it is the exact fault #534 fixed: invoices printing
 * a GSTIN that is not the seller's. So it shows as a value, and only becomes a
 * field after Change GSTIN.
 *
 * Each registration is its own form with its own idempotency key per body
 * (`lib/write-attempt.ts`), because the screen stays open and is saved more
 * than once.
 */

/** The API's bounds (`PutTaxIdentityDto`); a courtesy — the API decides. */
const NAME_MAX = 120;
const ADDRESS_LINE_MAX = 120;
const TITLE_MAX = 60;
const ADDRESS_LINES = 4;

const taxIdentitySchema = z.object({
  registeredName: z
    .string()
    .trim()
    .max(NAME_MAX, `A registered name is at most ${String(NAME_MAX)} characters.`),
  addressLines: z
    .array(
      z
        .string()
        .trim()
        .max(
          ADDRESS_LINE_MAX,
          `An address line is at most ${String(ADDRESS_LINE_MAX)} characters.`,
        ),
    )
    .length(ADDRESS_LINES),
  gstin: optionalGstinSchema,
  invoiceHeader: z
    .string()
    .trim()
    .max(TITLE_MAX, `An invoice title is at most ${String(TITLE_MAX)} characters.`),
});

type TaxIdentityValues = z.infer<typeof taxIdentitySchema>;

/** Blank is unset: `null` on the wire. */
const orNull = (value: string): string | null => (value === '' ? null : value);

const bodyOf = (values: TaxIdentityValues): TaxIdentityController_replace_v1Body => ({
  registeredName: orNull(values.registeredName),
  // A blank line between two is closed up rather than printed as a gap.
  addressLines: values.addressLines.filter((line) => line !== ''),
  gstin: orNull(values.gstin),
  invoiceHeader: orNull(values.invoiceHeader),
});

const valuesOf = (identity: TaxIdentityResource): TaxIdentityValues => ({
  registeredName: identity.registeredName ?? '',
  addressLines: Array.from(
    { length: ADDRESS_LINES },
    (_, index) => identity.addressLines[index] ?? '',
  ),
  gstin: identity.gstin ?? '',
  invoiceHeader: identity.invoiceHeader ?? '',
});

/** What a till still waits on, in the words the form's labels use. */
function missingFrom(identity: TaxIdentityResource): string[] {
  return [
    ...(identity.registeredName === null ? ['registered name'] : []),
    ...(identity.addressLines.length === 0 ? ['address'] : []),
    ...(identity.gstin === null ? ['GSTIN'] : []),
    ...(identity.invoiceHeader === null ? ['invoice title'] : []),
  ];
}

/** How a registration is named before it has a name. */
const UNNAMED = 'Registration not yet named';

type Loaded =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly identities: readonly TaxIdentityResource[];
      /** The one just saved, so only its section says so. */
      readonly savedId: string | null;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

export function TaxIdentities() {
  const client = useApiClient();
  const { session } = useSession();
  const mayEdit = session?.user.role === 'OWNER';

  const [state, setState] = useState<Loaded>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(TaxIdentityController_list_v1, { signal: controller.signal })
      .then((identities) => {
        if (live) setState({ kind: 'loaded', identities, savedId: null });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  const onSaved = (saved: TaxIdentityResource) => {
    setState((current) =>
      current.kind === 'loaded'
        ? {
            kind: 'loaded',
            identities: current.identities.map((identity) =>
              identity.id === saved.id ? saved : identity,
            ),
            savedId: saved.id,
          }
        : current,
    );
  };

  return (
    <Screen
      title="Tax identities"
      description="The GST registrations the park trades under. Each outlet names one, and its tills print that registration’s name, address and GSTIN at the top of every tax invoice."
      guidance={
        <>
          <ScreenNote label="Which outlet prints which">
            Set under Outlets: every outlet names its registration there, and the outlets list shows
            it beside each one.
          </ScreenNote>
          <ScreenNote label="Until all four are set">
            A till asks for all four before it prints a tax invoice, and is refused while any is
            missing — it never prints one without a GSTIN, or with another registration’s.
          </ScreenNote>
          <ScreenNote label="The GSTIN">
            Changed only through its own step, because a wrong one prints on every invoice its
            outlets issue. It is stored in capitals whatever case it is typed in.
          </ScreenNote>
          <ScreenNote label="Changing one">
            Every save is recorded with what it replaced. An invoice already printed keeps what it
            printed, and the tills pick the change up within fifteen minutes.
          </ScreenNote>
        </>
      }
    >
      {state.kind === 'loading' && <FormSkeleton />}

      {state.kind === 'failed' && (
        <div className="space-y-3 py-8 text-center">
          <LoadFailed
            error={state.error}
            onRetry={reload}
            title="The tax identities could not be loaded"
          />
        </div>
      )}

      {state.kind === 'loaded' &&
        (state.identities.length === 0 ? (
          <EmptyState
            title="No GST registrations on record"
            description="Until there is one, no outlet can print a tax invoice. Registrations are not created from this screen."
          />
        ) : (
          <div className="space-y-10">
            {state.identities.map((identity, index) => (
              <Registration
                key={identity.id}
                identity={identity}
                ruled={index > 0}
                saved={state.savedId === identity.id}
              >
                {mayEdit ? (
                  // Keyed on the stored moment, so a save re-opens the form on
                  // what the server kept — trimmed, upper-cased, closed up.
                  <TaxIdentityForm
                    key={identity.updatedAt ?? 'unset'}
                    identity={identity}
                    onSaved={onSaved}
                  />
                ) : (
                  <TaxIdentityRecord identity={identity} />
                )}
              </Registration>
            ))}
          </div>
        ))}
    </Screen>
  );
}

/** One registration: its name as a heading, whether a till can print from it, then its fields. */
function Registration({
  identity,
  ruled,
  saved,
  children,
}: {
  identity: TaxIdentityResource;
  ruled: boolean;
  saved: boolean;
  children: ReactNode;
}) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className={ruled ? 'space-y-5 border-t border-border pt-10' : 'space-y-5'}
    >
      {/* Body size, distinguished by weight: a screen has one size increase, at its title. */}
      <h2 id={headingId} className="font-medium text-foreground">
        {identity.registeredName ?? UNNAMED}
      </h2>
      <Standing identity={identity} saved={saved} />
      {children}
    </section>
  );
}

/** Whether a till can print from this, said before the fields. */
function Standing({ identity, saved }: { identity: TaxIdentityResource; saved: boolean }) {
  const missing = missingFrom(identity);

  if (missing.length > 0) {
    return (
      <Alert>
        <IconInfoCircle />
        <AlertTitle>No till at its outlets can issue a tax invoice yet</AlertTitle>
        <AlertDescription>
          Still to set: {new Intl.ListFormat('en-IN', { type: 'conjunction' }).format(missing)}.
          {saved && ' What was filled in has been saved.'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert role={saved ? 'status' : undefined}>
      <IconCircleCheck />
      <AlertTitle>
        {saved ? 'Saved.' : 'Set.'} Its outlets print this at the top of every tax invoice.
      </AlertTitle>
      {identity.updatedAt !== null && (
        <AlertDescription>Last set {formatInstant(identity.updatedAt)}.</AlertDescription>
      )}
    </Alert>
  );
}

/** A manager's view: the four values, and nothing to change them with. */
function TaxIdentityRecord({ identity }: { identity: TaxIdentityResource }) {
  const notSet = <span className="text-muted-foreground">Not set</span>;

  return (
    <dl className="space-y-4 text-sm">
      <div className="space-y-1.5">
        <dt className={fieldLabel}>Registered name</dt>
        <dd className="text-foreground">{identity.registeredName ?? notSet}</dd>
      </div>
      <div className="space-y-1.5">
        <dt className={fieldLabel}>Address</dt>
        <dd className="text-foreground">
          {identity.addressLines.length === 0
            ? notSet
            : identity.addressLines.map((line, index) => <p key={index}>{line}</p>)}
        </dd>
      </div>
      <div className="space-y-1.5">
        <dt className={fieldLabel}>GSTIN</dt>
        <dd className="font-mono tracking-wider text-foreground numeric">
          {identity.gstin ?? notSet}
        </dd>
      </div>
      <div className="space-y-1.5">
        <dt className={fieldLabel}>Invoice title</dt>
        <dd className="text-foreground">{identity.invoiceHeader ?? notSet}</dd>
      </div>
    </dl>
  );
}

function TaxIdentityForm({
  identity,
  onSaved,
}: {
  identity: TaxIdentityResource;
  onSaved: (identity: TaxIdentityResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(taxIdentitySchema),
    mode: 'onTouched',
    defaultValues: valuesOf(identity),
  });

  const [formError, setFormError] = useState<string | null>(null);

  // Whether the GSTIN has been opened for change. It stays a value until then.
  const [changingGstin, setChangingGstin] = useState(false);

  // Per body, not per mount: this form is saved again after a correction, and
  // a key held across two different bodies is refused as reused.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: TaxIdentityValues) => {
    if (pending) return;

    setFormError(null);

    const body = bodyOf(values);
    const sending = attemptFor(attempt.current, JSON.stringify(body));
    attempt.current = sending;

    try {
      const saved = await client.request(TaxIdentityController_replace_v1, {
        path: { id: identity.id },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
      });

      attempt.current = null;
      onSaved(saved);
    } catch (error) {
      setFormError(messageForFailure(error));
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="registeredName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={fieldLabel}>Registered name</FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    placeholder="GREEN WALK"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>The name the GSTIN is registered to, as it prints.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {Array.from({ length: ADDRESS_LINES }, (_, index) => (
          <FormField
            key={index}
            control={form.control}
            name={`addressLines.${index}`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>Address line {index + 1}</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      placeholder={ADDRESS_EXAMPLES[index]}
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                {index === 0 && (
                  <FormDescription>
                    Printed in this order, up to four lines. A blank line is left out.
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        ))}

        {changingGstin ? (
          <FormField
            control={form.control}
            name="gstin"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>GSTIN</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                      placeholder="32ABBFG0274A1Z8"
                      autoComplete="off"
                      autoCapitalize="characters"
                      {...field}
                      onChange={(event) => {
                        // Upper-cased as typed, because it is stored that way —
                        // otherwise the save comes back looking changed.
                        field.onChange(event.target.value.toUpperCase());
                      }}
                    />
                  }
                />
                <FormDescription>
                  15 characters, exactly as on the registration certificate. Every tax invoice this
                  registration’s outlets print carries it.
                </FormDescription>
                <FormMessage />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.resetField('gstin');
                    setChangingGstin(false);
                  }}
                >
                  Keep the current GSTIN
                </Button>
              </FormItem>
            )}
          />
        ) : (
          <div className="space-y-1.5">
            <p className={fieldLabel}>GSTIN</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="font-mono tracking-wider text-foreground numeric">
                {identity.gstin ?? <span className="font-sans text-muted-foreground">Not set</span>}
              </p>
              <Button type="button" variant="outline" onClick={() => setChangingGstin(true)}>
                {identity.gstin === null ? 'Set GSTIN' : 'Change GSTIN'}
              </Button>
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="invoiceHeader"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={fieldLabel}>Invoice title</FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    placeholder="TAX INVOICE"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>The line printed above the invoice.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Above the submit and it stays there — a toast vanishes. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <div className="pt-3">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

/** The shape of a good answer, per line — the garden's own address. */
const ADDRESS_EXAMPLES = [
  'GOVT. BOTANICAL GARDEN',
  'MUNNAR',
  'IDUKKI DISTRICT, KERALA',
  'PIN 685612',
];

/** The form's shape while it is read: labels and fields, no spinner. */
function FormSkeleton() {
  return (
    <div aria-hidden className="space-y-5">
      {[40, 36, 36, 36, 36, 28, 32].map((width, index) => (
        <div key={index} className="space-y-2">
          <div
            className="h-3 animate-pulse rounded-3xl bg-muted"
            style={{ width: `${String(width)}%` }}
          />
          <div className="h-11 animate-pulse rounded-3xl bg-muted" />
        </div>
      ))}
    </div>
  );
}
